import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { DollarSign, CheckCircle, XCircle, RotateCcw, TrendingUp, Loader, RefreshCw } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const generateMockMonthlyData = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    const revenue = Math.round(300 + Math.random() * 800);
    const total = Math.round(3 + Math.random() * 8);
    const failed = Math.round(Math.random() * 2);
    const refunded = Math.round(Math.random() * 1);
    return {
      month: MONTHS?.[d?.getMonth()],
      revenue,
      total,
      succeeded: total - failed - refunded,
      failed,
      refunded,
    };
  });
};

const PaymentMetrics = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('revenue');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabase || !user?.id) {
        const mock = generateMockMonthlyData();
        setMonthlyData(mock);
        const totalRevenue = mock?.reduce((s, m) => s + m?.revenue, 0);
        const totalTx = mock?.reduce((s, m) => s + m?.total, 0);
        const totalSucceeded = mock?.reduce((s, m) => s + m?.succeeded, 0);
        const totalFailed = mock?.reduce((s, m) => s + m?.failed, 0);
        const totalRefunded = mock?.reduce((s, m) => s + m?.refunded, 0);
        setMetrics({
          totalRevenue,
          successRate: totalTx > 0 ? ((totalSucceeded / totalTx) * 100)?.toFixed(1) : '0.0',
          failedCount: totalFailed,
          refundRate: totalTx > 0 ? ((totalRefunded / totalTx) * 100)?.toFixed(1) : '0.0',
          totalTransactions: totalTx,
        });
        return;
      }

      // Fetch all payment_transactions for this user
      const { data: txData, error: txError } = await supabase?.from('payment_transactions')?.select('id, amount, status, created_at')?.eq('user_id', user?.id)?.order('created_at', { ascending: true });

      // Fetch orders for refund data
      const { data: ordersData } = await supabase?.from('orders')?.select('id, total_amount, status, created_at')?.eq('user_id', user?.id)?.order('created_at', { ascending: true });

      const transactions = txError ? [] : (txData || []);
      const orders = ordersData || [];

      // Use mock if no real data
      if (transactions?.length === 0 && orders?.length === 0) {
        const mock = generateMockMonthlyData();
        setMonthlyData(mock);
        const totalRevenue = mock?.reduce((s, m) => s + m?.revenue, 0);
        const totalTx = mock?.reduce((s, m) => s + m?.total, 0);
        const totalSucceeded = mock?.reduce((s, m) => s + m?.succeeded, 0);
        const totalFailed = mock?.reduce((s, m) => s + m?.failed, 0);
        const totalRefunded = mock?.reduce((s, m) => s + m?.refunded, 0);
        setMetrics({
          totalRevenue,
          successRate: totalTx > 0 ? ((totalSucceeded / totalTx) * 100)?.toFixed(1) : '0.0',
          failedCount: totalFailed,
          refundRate: totalTx > 0 ? ((totalRefunded / totalTx) * 100)?.toFixed(1) : '0.0',
          totalTransactions: totalTx,
        });
        return;
      }

      // Compute overall metrics
      const normalizeAmount = (a) => (a > 1000 ? Math.round(a / 100) : a);
      const succeeded = transactions?.filter(t => t?.status === 'succeeded');
      const failed = transactions?.filter(t => t?.status === 'failed');
      const refundedOrders = orders?.filter(o => o?.status === 'refunded');
      const totalRevenue = succeeded?.reduce((s, t) => s + normalizeAmount(t?.amount || 0), 0);
      const totalTx = transactions?.length;
      const successRate = totalTx > 0 ? ((succeeded?.length / totalTx) * 100)?.toFixed(1) : '0.0';
      const refundRate = totalTx > 0 ? ((refundedOrders?.length / totalTx) * 100)?.toFixed(1) : '0.0';

      setMetrics({
        totalRevenue,
        successRate,
        failedCount: failed?.length,
        refundRate,
        totalTransactions: totalTx,
      });

      // Build monthly trend (last 6 months)
      const now = new Date();
      const monthMap = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        monthMap[key] = { month: MONTHS?.[d?.getMonth()], revenue: 0, total: 0, succeeded: 0, failed: 0, refunded: 0 };
      }

      transactions?.forEach(tx => {
        if (!tx?.created_at) return;
        const d = new Date(tx.created_at);
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        if (!monthMap?.[key]) return;
        monthMap[key].total += 1;
        if (tx?.status === 'succeeded') {
          monthMap[key].succeeded += 1;
          monthMap[key].revenue += normalizeAmount(tx?.amount || 0);
        } else if (tx?.status === 'failed') {
          monthMap[key].failed += 1;
        }
      });

      orders?.forEach(o => {
        if (!o?.created_at || o?.status !== 'refunded') return;
        const d = new Date(o.created_at);
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        if (!monthMap?.[key]) return;
        monthMap[key].refunded += 1;
      });

      setMonthlyData(Object.values(monthMap));
    } catch (err) {
      console.error('Error fetching payment metrics:', err);
      const mock = generateMockMonthlyData();
      setMonthlyData(mock);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })?.format(v || 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-xl p-3 shadow-lg text-sm">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload?.map((p, i) => (
          <p key={i} style={{ color: p?.color }} className="text-xs">
            {p?.name}: {p?.name === 'Revenue' ? formatCurrency(p?.value) : p?.value}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Revenue',
      value: formatCurrency(metrics?.totalRevenue),
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      sub: `${metrics?.totalTransactions || 0} total transactions`,
    },
    {
      label: 'Success Rate',
      value: `${metrics?.successRate}%`,
      icon: CheckCircle,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      sub: 'Successful payments',
    },
    {
      label: 'Failed Payments',
      value: metrics?.failedCount ?? 0,
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      sub: 'Requires attention',
    },
    {
      label: 'Refund Rate',
      value: `${metrics?.refundRate}%`,
      icon: RotateCcw,
      color: 'text-orange-600',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      sub: 'Of total transactions',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards?.map((card, i) => {
          const CardIcon = card?.icon;
          return (
            <div key={i} className={`bg-card border ${card?.border} rounded-2xl p-5 shadow-elevation-1`}>
              <div className={`w-10 h-10 ${card?.bg} rounded-xl flex items-center justify-center mb-3`}>
                <CardIcon className={`w-5 h-5 ${card?.color}`} />
              </div>
              <p className="text-2xl font-bold text-foreground">{card?.value}</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{card?.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card?.sub}</p>
            </div>
          );
        })}
      </div>
      {/* Monthly Trend Chart */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-elevation-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Monthly Payment Trends</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-lg p-1 gap-1">
              {[
                { key: 'revenue', label: 'Revenue' },
                { key: 'transactions', label: 'Transactions' },
              ]?.map(opt => (
                <button
                  key={opt?.key}
                  onClick={() => setChartType(opt?.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    chartType === opt?.key
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
            <button
              onClick={fetchMetrics}
              className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {chartType === 'revenue' ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                tickFormatter={v => `$${v >= 1000 ? `${(v / 1000)?.toFixed(1)}k` : v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="url(#revenueGrad)"
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="succeeded" name="Succeeded" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="refunded" name="Refunded" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* Chart Legend Summary */}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
          {[
            { label: 'Avg Monthly Revenue', value: formatCurrency(monthlyData?.length ? monthlyData?.reduce((s, m) => s + m?.revenue, 0) / monthlyData?.length : 0), color: 'text-blue-600' },
            { label: 'Avg Success/Month', value: monthlyData?.length ? (monthlyData?.reduce((s, m) => s + m?.succeeded, 0) / monthlyData?.length)?.toFixed(1) : '0', color: 'text-green-600' },
            { label: 'Avg Failed/Month', value: monthlyData?.length ? (monthlyData?.reduce((s, m) => s + m?.failed, 0) / monthlyData?.length)?.toFixed(1) : '0', color: 'text-red-600' },
          ]?.map((item, i) => (
            <div key={i} className="text-center">
              <p className={`text-base font-bold ${item?.color}`}>{item?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item?.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentMetrics;
